import React, { useState } from 'react';
import { SegmentedControl } from '@mantine/core';
import Button from '@srm/ui/button';
import Hint from '@srm/ui/hint';
import Modal from '@srm/ui/modal';
import FormGroup from '@srm/ui/form-group';
import { EditableResult, IMatrixLot, IMatrixParticipant } from '../WinnerSelection2.types';
import { formatMatrixMessage } from '../utils/matrixView';
import { DialogContext } from './DialogContext';

interface IProps {
    lot: IMatrixLot;
    participant: IMatrixParticipant;
    hiddenPositions: number;
    busy: boolean;
    blocked?: boolean;
    error: string | undefined;
    onDecide: (result: EditableResult) => void;
    onClose: () => void;
}

/** Оценка всех предложений участника в лоте: сервер применяет её ко всему лоту или не применяет вовсе. */
const LotDecisionDialog: React.FC<IProps> = ({
    lot,
    participant,
    hiddenPositions,
    busy,
    blocked,
    error,
    onDecide,
    onClose,
}) => {
    const [result, setResult] = useState<EditableResult | null>(null);
    const summary = lot.participants.find((item) => item.participantKey === participant.key);
    const canDecide = !!summary?.canDecideLot;
    const canReset = !!summary?.canResetLot;

    const footer = (
        <div className="ws2-dialog__footer">
            <Button variant="outline-neutral" disabled={busy} onClick={onClose}>
                Отмена
            </Button>
            <Button
                variant="primary"
                disabled={busy || blocked || result == null || (result === 'none' ? !canReset : !canDecide)}
                loading={busy}
                onClick={() => result != null && onDecide(result)}
            >
                {result === 'none' ? 'Сбросить выбор' : 'Применить'}
            </Button>
        </div>
    );

    return (
        <Modal
            className="ws2-modal"
            width={640}
            size="l"
            header="Оценить весь лот"
            onClose={onClose}
            hasUnsavedInput={() => result !== null}
            closeOnEscape={!busy}
            closeDisabled={busy}
            footer={footer}
        >
            <div className="ws2-dialog">
                <DialogContext supplier={participant.name} subject={lot.title} />
                {error && <Hint role="alert" title={error} tone="danger" maxWidth="100%" />}
                <p className="ws2-dialog__note">
                    Выбор применяется ко всем позициям лота ({lot.positionKeys.length})
                    {hiddenPositions > 0 ? `, включая скрытые поиском или свёрнутые (${hiddenPositions})` : ''}. При
                    назначении используется предложенное количество
                    {lot.fixedQuantity ? '' : '. Его можно изменить в деталях позиции'}
                </p>
                {!canDecide && summary?.lotDecisionReason && (
                    <Hint title={formatMatrixMessage(summary.lotDecisionReason)} tone="warning" maxWidth="100%" />
                )}
                <FormGroup label="Результат выбора">
                    <SegmentedControl
                        className="ws2-dialog__result-segments"
                        aria-label="Результат выбора для лота"
                        fullWidth
                        radius="sm"
                        data={[
                            { value: 'main', label: 'Основной', disabled: !canDecide },
                            { value: 'reserve', label: 'Резервный', disabled: !canDecide },
                            ...(canReset ? [{ value: 'none', label: 'Сбросить выбор' }] : []),
                        ]}
                        value={result ?? ''}
                        disabled={busy || blocked}
                        onChange={(value) => setResult(value as EditableResult)}
                    />
                </FormGroup>
                {result === 'none' && (
                    <p className="ws2-dialog__note">
                        Сброс очистит результат и количество во всём лоте. Ответы по ТЗ сохранятся
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default LotDecisionDialog;
